import React, {
  useCallback,
  useEffect,
  useReducer,
  useRef,
  useState,
} from "react";
import {
  Button,
  Grid,
  Label,
  Search,
  SearchResultProps,
} from "semantic-ui-react";
import {
  TkrCusip,
  InstrumentParams,
  PortfolioEntry,
  Slice,
} from "../../../types";
import { Reducer } from "redux";
import _ from "lodash";
import {
  CLEAN_QUERY,
  FINISH_SEARCH,
  START_SEARCH,
  UPDATE_SELECTION,
} from "./constants";
import styles from "../styles.module.scss";
import moment from "moment";
import { fileDataFormat } from "../../../utils/dateFormat";
import { Link } from "react-router-dom";

interface Props {
  cusipTkrCollection: PortfolioEntry[];
  fetchInstrumentDetail: (
    params: InstrumentParams,
    token: string | null
  ) => void;
  date: string;
  slice: string;
  token: string | null;
  setTkrCusip: (tkrCusip: TkrCusip) => void;
  handleSliceChange: (slice: Slice) => void;
  urlBase: string;
}

interface SearchState {
  loading: boolean;
  results: [];
  value: string;
}

const initialState: SearchState = {
  loading: false,
  results: [],
  value: "",
};

const reducer: Reducer<SearchState> = (state = initialState, action) => {
  switch (action.type) {
    case CLEAN_QUERY:
      return initialState;
    case START_SEARCH:
      return { ...state, loading: true, value: action.query };
    case FINISH_SEARCH:
      return { ...state, loading: false, results: action.results };
    case UPDATE_SELECTION:
      return { ...state, value: action.selection };
    default:
      throw new Error();
  }
};

const resultRenderer = (searchProps: SearchResultProps) => (
  <Label
    key={searchProps.id}
    content={`${searchProps.tkr} - ${searchProps.cusip}`}
  />
);

const InstrumentSelector = ({
  cusipTkrCollection,
  fetchInstrumentDetail,
  date,
  slice,
  token,
  setTkrCusip,
  handleSliceChange,
  urlBase,
}: Props) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { loading, results, value } = state;
  const [tkrCusip, setTickerCusip] = useState<TkrCusip>();

  const timer: React.MutableRefObject<any> = useRef();

  const handleSearchChange = useCallback(
    (e, data) => {
      clearTimeout(timer.current);
      dispatch({ type: START_SEARCH, query: data.value });
      timer.current = setTimeout(() => {
        if (data.value === 0) {
          dispatch({ type: CLEAN_QUERY });
          return;
        }

        const re = new RegExp(_.escapeRegExp(data.value), "i");
        const isMatch = (result: PortfolioEntry) =>
          re.test(result.tkr) || re.test(result.cusip);

        dispatch({
          type: FINISH_SEARCH,
          results: _.filter(cusipTkrCollection, isMatch).map((result) => {
            return {
              title: `${result.tkr}`,
              key: `${result.tkr}-${result.cusip}`,
              reqchq: result.reqChg,
              amtchg: result.amtChg,
              curamt: result.curAmt,
              securitytype: result.securityType,
              tkr: result.tkr,
              cusip: result.cusip,
            };
          }),
        });
      }, 300);
    },
    [cusipTkrCollection]
  );
  useEffect(() => {
    return () => {
      clearTimeout(timer.current);
    };
  }, [timer]);

  return (
    <Grid columns={2} className={styles.searchbar}>
      <Grid.Column width={7} className={styles.search}>
        <Search
          size={"large"}
          loading={loading}
          onResultSelect={(e, data) => {
            dispatch({
              type: UPDATE_SELECTION,
              selection: `${data.result.tkr} - ${data.result.cusip}`,
            });
            setTickerCusip({ cusip: data.result.cusip, tkr: data.result.tkr });
            setTkrCusip({ cusip: data.result.cusip, tkr: data.result.tkr });
          }}
          onSearchChange={handleSearchChange}
          resultRenderer={resultRenderer}
          results={results}
          value={value}
        />
      </Grid.Column>
      <Grid.Column className={styles.button}>
        {cusipTkrCollection.length > 0 && (
          <Link
            to={`${urlBase}/${date}/${slice}/${tkrCusip?.tkr}/${tkrCusip?.cusip}?level=instrument`}
          >
            <Button
              color={"blue"}
              onClick={() => {
                fetchInstrumentDetail(
                  {
                    tkrCusip: {
                      tkr: tkrCusip?.tkr as string,
                      cusip: tkrCusip?.cusip as string,
                    },
                    date: date,
                    slice: slice,
                  },
                  token
                );
                handleSliceChange({
                  date: moment(date).format(fileDataFormat),
                  id: slice,
                });
              }}
            >
              View Instrument
            </Button>
          </Link>
        )}
      </Grid.Column>
    </Grid>
  );
};
export default InstrumentSelector;
